import React, { useCallback, useMemo } from "react";
import styles from "./index.module.scss";
import { TooltipBaseV2, TooltipProps } from "components/tooltips/TooltipBaseV2";

export interface SwitchProps {
    /** The unique identifier for the toggle switch - for backwards compatibility, if no id is given, the string label will be used*/
    id?: string;
    on: boolean;
    onToggle: (state?: boolean) => void;
    /** The label to display next to the toggle switch - if label is an Element, accompanying id prop is required */
    label?: string | JSX.Element;
    disabled?: boolean;
    condensed?: boolean;
    isOnOn?: boolean;
    helpTooltip?: JSX.Element | TooltipProps;
    disabledTooltip?: JSX.Element | TooltipProps;
    reverseLayout?: boolean;
    className?: string;
}

/** A simple toggle component. */
export const Toggle: React.FC<SwitchProps> = ({
    id,
    label,
    on,
    onToggle,
    disabled = false,
    condensed = false,
    isOnOn = false,
    helpTooltip = null,
    disabledTooltip = null,
    reverseLayout = false,
    className = ""
}: SwitchProps) => {
    // Backwards compatibility to account for new id prop and Element label type
    if (!id && typeof label === "string") id = label;

    const _onKeyDown = useCallback(
        (event) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onToggle();
            }
        },
        [onToggle]
    );

    const isTooltipProps = (
        tooltip: JSX.Element | TooltipProps
    ): tooltip is TooltipProps => {
        return (tooltip as TooltipProps)?.body !== undefined;
    };

    const helpTooltipComponent = useMemo(() => {
        if (!helpTooltip) return <></>;
        return isTooltipProps(helpTooltip) ? (
            <TooltipBaseV2
                show
                type={helpTooltip.type}
                body={helpTooltip.body}
                label={helpTooltip.label}
            />
        ) : (
            helpTooltip
        );
    }, [helpTooltip]);

    const disabledTooltipComponent = useMemo(() => {
        if (!disabledTooltip) return <></>;
        return isTooltipProps(disabledTooltip) ? (
            <TooltipBaseV2
                show
                type={disabledTooltip.type}
                body={disabledTooltip.body}
                label={disabledTooltip.label}
            />
        ) : (
            disabledTooltip
        );
    }, [disabledTooltip]);

    const switchComponent = useMemo(
        () => (
            <div
                className={`${styles["bar"]} ${
                    isOnOn || on ? styles["transform-bar"] : ""
                }`}
            >
                <div
                    className={`${styles["switch"]} ${
                        isOnOn || on ? styles["switch-on"] : ""
                    } ${on ? styles["transform-switch"] : ""}`}
                ></div>
                <input
                    id={`toggle-switch-${id}`}
                    type="checkbox"
                    checked={isOnOn || on}
                    disabled={disabled}
                    onChange={() => onToggle(!on)}
                    className={"sr-only"}
                    onKeyDown={_onKeyDown}
                />
            </div>
        ),
        [isOnOn, on, onToggle, disabled, id, _onKeyDown]
    );

    const labelComponent = useMemo(
        () => (
            <label
                className={`${styles["switch-label"]}`}
                htmlFor={`toggle-switch-${id}`}
                onClick={(e) => e.stopPropagation()}
            >
                {label}
            </label>
        ),
        [label, id]
    );

    const labelWithTooltipComponent = useMemo(() => {
        if (helpTooltip && !on && !disabled) {
            return React.cloneElement(helpTooltipComponent, {
                children: labelComponent
            });
        } else if (disabledTooltip && disabled) {
            return React.cloneElement(disabledTooltipComponent, {
                children: labelComponent
            });
        } else {
            return labelComponent;
        }
    }, [
        disabled,
        disabledTooltip,
        disabledTooltipComponent,
        helpTooltip,
        helpTooltipComponent,
        labelComponent,
        on
    ]);

    return (
        <>
            <div
                className={`switch-container ${
                    styles["switch-container"]
                } ${className} ${
                    disabled ? `disabled ${styles["disabled"]}` : ""
                } ${condensed ? styles["condensed"] : ""}
                 ${reverseLayout ? styles["reverse-layout"] : ""}`}
                onClick={() => !disabled && onToggle(!on)}
            >
                {reverseLayout ? (
                    <>
                        {switchComponent}
                        {labelWithTooltipComponent}
                    </>
                ) : (
                    <>
                        {labelWithTooltipComponent}
                        {switchComponent}
                    </>
                )}
            </div>
        </>
    );
};

export const ToggleCard = ({ title, desc }) => {
    return (
        <div className={styles["option-card"]}>
            <h3 className={styles["card-title"]}>{title}</h3>
            <p className={styles["card-description"]}>{desc}</p>
        </div>
    );
};
